/**
 * /bewerbung — Facebook-Ads Landingpage: Rückruf-Anfrage (kein Termin).
 *
 * Funnel-Psychologie (skeptische deutsche Zielgruppe, arbeitssuchend):
 *  - durchgehend Sie-Ansprache (Zielgruppe ist überwiegend älter)
 *  - leichte, unverfängliche Fragen zuerst (Name → Situation → Führerschein),
 *    Telefonnummer erst am Ende, wenn Commitment + Vertrauen da sind
 *  - „wer ruft an" + „so läuft der Anruf" direkt beim Telefon-Feld
 *  - „Warum kostenlos?" + „Wer erfährt davon?" werden explizit beantwortet
 *  - eine Frage pro Screen, Enter = weiter, Auswahl springt automatisch
 *
 * Tracking: Meta Pixel (PageView, LeadFormStart, Lead mit eventID) und ChatGPT-Ads-Pixel
 * (lead_created mit event_id) + Funnel-Logs. Beide Conversions gehen zusaetzlich
 * serverseitig ueber die jeweilige Conversions API raus, mit derselben ID → dedupliziert.
 * Server: POST /api/lead → SQLite, Pipedrive, Meta CAPI, OpenAI CAPI, Mails.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPixelEvent, trackPixelCustom, setMetaUserData, getMetaBrowserIds, generateEventId } from '../../lib/metaPixel';
import { trackOpenAiLead } from '../../lib/openaiPixel';

const LOGO = 'https://zba.academy/webinar/images/zbalogo2.png';
const TOTAL_STEPS = 6;
const DRAFT_KEY = 'zba_lead_draft_v4';
const PAGE_TITLE = 'Sprinklertechniker werden – kostenlos mit Bildungsgutschein | ZBA Academy';

// Reihenfolge nach Messung: 98 % brachen vorher bei der ersten Frage ab, weil sie
// Tippen verlangte. Jetzt kommen die Antipp-Fragen zuerst (ein Fingertipp, kein
// Tastenfeld), die Tipp-Felder danach. Die Einwilligung sitzt beim Absenden.
const STEP_NAMES = { 1: 'lead_arbeitslos', 2: 'lead_fuehrerschein', 3: 'lead_name', 4: 'lead_email', 5: 'lead_phone', 6: 'lead_reach', 7: 'lead_success' };

const REACH_OPTIONS = [
  { id: 'vormittags',  icon: '🌤', label: 'Vormittags',  sub: '8–12 Uhr' },
  { id: 'nachmittags', icon: '☀️', label: 'Nachmittags', sub: '12–16 Uhr' },
  { id: 'abends',      icon: '🌆', label: 'Abends',      sub: '16–18 Uhr' },
  { id: 'egal',        icon: '📞', label: 'Jederzeit',   sub: 'Ruft einfach an' },
];

const FAQ = [
  { q: 'Kostet mich das etwas?', a: 'Nein. Der Anruf ist kostenlos und unverbindlich. Die Qualifizierung selbst wird über den Bildungsgutschein von der Agentur für Arbeit oder dem Jobcenter bezahlt – für Sie fallen keine Kosten an.' },
  { q: 'Muss ich mich sofort entscheiden?', a: 'Nein. Im Anruf erklären wir Ihnen in Ruhe, was der Beruf ist und wie die Förderung läuft. Sie entscheiden danach – ohne Druck.' },
  { q: 'Erfährt mein Jobcenter oder die Agentur davon?', a: 'Nicht von uns. Wir geben Ihre Daten nicht weiter. Erst wenn Sie es möchten, helfen wir Ihnen beim Antrag für den Bildungsgutschein.' },
  { q: 'Brauche ich Vorkenntnisse?', a: 'Das klären wir gemeinsam im Gespräch. Wichtiger als Vorwissen sind Motivation, Deutschkenntnisse für den Arbeitsalltag und die Bereitschaft, auf Baustellen zu arbeiten.' },
];

const CSS = `
.lf { min-height: 100vh; background: #f0f2f5; font-family: var(--font-ui, 'Outfit', system-ui, sans-serif); color: #1e2b38; display: flex; flex-direction: column; }
.lf * { box-sizing: border-box; }
.lf-wrap { width: 100%; max-width: 520px; margin: 0 auto; padding: 20px 16px 24px; flex: 1;
  display: flex; flex-direction: column; }
.lf-top { order: 1; } .lf-hero { order: 2; } .lf-card { order: 3; } .lf-trust { order: 4; }
.lf-how { order: 5; } .lf-faq { order: 6; }
@media (min-width: 640px) { .lf-wrap { padding: 36px 24px 32px; } }

.lf-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
.lf-logo { height: 34px; width: auto; display: block; }
.lf-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px; border-radius: 100px; background: #e6f4ea; color: #15803d; font-size: 11.5px; font-weight: 700; letter-spacing: .03em; white-space: nowrap; }
.lf-pill .dot { width: 6px; height: 6px; border-radius: 50%; background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,.18); }

.lf-hero { text-align: left; margin-bottom: 14px; }
.lf-h1 { font-family: var(--font-heading, 'Fraunces', Georgia, serif); font-size: 26px; line-height: 1.15; font-weight: 700; letter-spacing: -.02em; margin: 0 0 10px; color: #1e2b38; text-wrap: balance; }
.lf-h1 em { font-style: normal; color: #3E4954; background: linear-gradient(transparent 62%, rgba(62,73,84,.16) 62%); }
@media (min-width: 640px) { .lf-h1 { font-size: 34px; } }
.lf-sub { font-size: 15px; line-height: 1.5; color: #475569; margin: 0 0 12px; }
.lf-trust { display: flex; flex-wrap: wrap; gap: 6px 14px; padding: 0; margin: 14px 0 0; list-style: none; justify-content: center; }
.lf-trust li { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: #6b7a8d; line-height: 1.3; }
.lf-trust li svg { flex-shrink: 0; margin-top: 3px; }
.lf-trust li span b { color: #1e2b38; }

.lf-card { background: #fff; border: 1px solid #dde1e7; border-radius: 22px; padding: 22px 20px 20px; box-shadow: 0 8px 32px rgba(62,73,84,.12), 0 2px 8px rgba(62,73,84,.06); position: relative; overflow: hidden; }
@media (min-width: 640px) { .lf-card { padding: 28px 28px 26px; } }

.lf-progress { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.lf-bar { flex: 1; height: 6px; background: #eef0f3; border-radius: 100px; overflow: hidden; }
.lf-bar > i { display: block; height: 100%; background: linear-gradient(90deg, #3E4954, #5b6b7c); border-radius: 100px; transition: width 380ms cubic-bezier(.22,1,.36,1); }
.lf-step { font-size: 11.5px; font-weight: 700; color: #6b7a8d; letter-spacing: .05em; text-transform: uppercase; white-space: nowrap; }

.lf-q { font-family: var(--font-heading, 'Fraunces', Georgia, serif); font-size: 23px; line-height: 1.2; font-weight: 700; letter-spacing: -.015em; margin: 0 0 6px; text-wrap: balance; }
.lf-hint { font-size: 14px; color: #6b7a8d; line-height: 1.55; margin: 0 0 18px; }

.lf-label { display: block; font-size: 12.5px; font-weight: 700; color: #3E4954; margin: 0 0 6px; }
.lf-input { width: 100%; height: 54px; padding: 0 16px; border: 1.5px solid #dde1e7; border-radius: 14px; font: inherit; font-size: 17px; color: #1e2b38; background: #fff; outline: none; transition: border-color 160ms ease, box-shadow 160ms ease; -webkit-appearance: none; }
.lf-input::placeholder { color: #a3adb9; }
.lf-input:focus { border-color: #3E4954; box-shadow: 0 0 0 4px rgba(62,73,84,.10); }
.lf-input.err { border-color: #dc2626; box-shadow: 0 0 0 4px rgba(220,38,38,.08); }
.lf-error { font-size: 13px; color: #dc2626; margin: 6px 0 0; font-weight: 600; }
.lf-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 380px) { .lf-grid2 { grid-template-columns: 1fr; } }
.lf-field { margin-bottom: 14px; }

.lf-choice { display: grid; gap: 10px; margin-bottom: 6px; }
.lf-choice.cols2 { grid-template-columns: 1fr 1fr; }
.lf-opt { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 60px; padding: 12px 16px; border-radius: 14px; border: 1.5px solid #dde1e7; background: #f8f9fa; color: #1e2b38; font: inherit; font-size: 16px; font-weight: 700; text-align: left; cursor: pointer; transition: all 140ms ease; -webkit-tap-highlight-color: transparent; }
.lf-opt .ic { font-size: 22px; line-height: 1; flex-shrink: 0; }
.lf-opt .sub { display: block; font-size: 12.5px; font-weight: 500; color: #6b7a8d; margin-top: 2px; }
.lf-opt:hover { border-color: #3E4954; background: #fff; }
.lf-opt.on { border-color: #3E4954; background: #3E4954; color: #fff; box-shadow: 0 6px 18px rgba(62,73,84,.28); }
.lf-opt.on .sub { color: rgba(255,255,255,.75); }
.lf-opt.compact { min-height: 52px; justify-content: center; text-align: center; }

.lf-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 58px; border: none; border-radius: 16px; background: #3E4954; color: #fff; font: inherit; font-size: 17px; font-weight: 700; letter-spacing: -.01em; cursor: pointer; box-shadow: 0 6px 20px rgba(62,73,84,.30); transition: transform 140ms ease, box-shadow 140ms ease, background 140ms ease; -webkit-tap-highlight-color: transparent; margin-top: 6px; }
.lf-btn:hover { background: #2d3a47; transform: translateY(-1px); box-shadow: 0 10px 26px rgba(62,73,84,.34); }
.lf-btn:active { transform: translateY(0); }
.lf-btn:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }
.lf-btn.cta { background: #16a34a; box-shadow: 0 6px 20px rgba(22,163,74,.32); height: 62px; font-size: 18px; }
.lf-btn.cta:hover { background: #15803d; }
.lf-btn .spin { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: lf-spin .8s linear infinite; }
@keyframes lf-spin { to { transform: rotate(360deg); } }

.lf-back { display: inline-flex; align-items: center; gap: 6px; margin-top: 14px; padding: 6px 2px; background: none; border: none; font: inherit; font-size: 13.5px; font-weight: 600; color: #6b7a8d; cursor: pointer; }
.lf-back:hover { color: #3E4954; }
.lf-dslink { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #6b7a8d; text-decoration: none; margin-bottom: 18px; }
.lf-dslink:hover { color: #3E4954; }
/* Einwilligungs-Checkbox in Schritt 1. Eigener Name: .lf-check gehoert bereits
   dem gruenen Haken auf dem Danke-Screen. */
.lf-agree { display: flex; width: 100%; align-items: flex-start; gap: 10px; padding: 2px 0 6px; cursor: pointer; margin-bottom: 12px; -webkit-tap-highlight-color: transparent; }
.lf-agree.err .box { border-color: #dc2626; }
.lf-agree.err .txt { color: #dc2626; }
.lf-agree input { position: absolute; opacity: 0; width: 1px; height: 1px; margin: 0; }
.lf-agree .box { width: 20px; height: 20px; border-radius: 5px; border: 2px solid #c5cdd6; background: #fff; display: flex; align-items: center; justify-content: center; flex: 0 0 20px; margin-top: 0; transition: border-color 160ms ease, background 160ms ease; }
.lf-agree.on .box { border-color: #3E4954; background: #3E4954; }
.lf-agree .txt { flex: 1 1 auto; min-width: 0; font-size: 12.5px; line-height: 1.45; color: #6b7a8d; font-weight: 500; }
.lf-agree .txt a { color: #6b7a8d; text-decoration: underline; text-underline-offset: 2px; }
.lf-agree input:focus-visible + .box { box-shadow: 0 0 0 4px rgba(62,73,84,.18); }
.lf-secure { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 12px; font-size: 12.5px; color: #6b7a8d; }
.lf-consent { font-size: 12px; line-height: 1.55; color: #6b7a8d; margin: 12px 0 0; text-align: center; }
.lf-consent a { color: #3E4954; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }

/* Berater-Karte: wer ruft an */

.lf-how { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 18px; }
.lf-how div { background: #fff; border: 1px solid #e6e9ee; border-radius: 14px; padding: 12px 10px; text-align: center; }
.lf-how b { display: block; font-size: 20px; line-height: 1; margin-bottom: 6px; }
.lf-how span { display: block; font-size: 12px; line-height: 1.35; font-weight: 600; color: #3E4954; }
.lf-how small { display: block; font-size: 11px; color: #6b7a8d; margin-top: 3px; }

.lf-faq { margin-top: 16px; background: #fff; border: 1px solid #e6e9ee; border-radius: 16px; overflow: hidden; }
.lf-faq h3 { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #6b7a8d; margin: 0; padding: 14px 16px 6px; }
.lf-faq details { border-top: 1px solid #eef0f3; }
.lf-faq summary { list-style: none; cursor: pointer; padding: 12px 16px; font-size: 14px; font-weight: 700; color: #1e2b38; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.lf-faq summary::-webkit-details-marker { display: none; }
.lf-faq summary::after { content: '+'; font-size: 18px; color: #6b7a8d; font-weight: 400; }
.lf-faq details[open] summary::after { content: '–'; }
.lf-faq p { margin: 0; padding: 0 16px 14px; font-size: 13.5px; line-height: 1.55; color: #475569; }

.lf-foot { text-align: center; padding: 20px 16px 28px; font-size: 11.5px; line-height: 1.7; color: #8a97a8; }
.lf-foot a { color: #6b7a8d; text-decoration: none; font-weight: 600; }
.lf-foot a:hover { color: #3E4954; }

/* Auf dem Handy zählt jeder Pixel über der Falz: die Formularkarte rückt vor die
   Vertrauens-Punkte, und Abstände werden gestrafft. Ziel ist, dass beide Namensfelder
   und der Weiter-Button ohne Scrollen sichtbar sind — auch im Facebook-In-App-Browser,
   der oben und unten je eine Leiste einblendet. */
@media (max-width: 640px) {
  .lf-wrap { padding-top: 14px; }
  .lf-top { margin-bottom: 12px; }
  .lf-logo { height: 30px; }
  .lf-hero { margin-bottom: 10px; }
  .lf-h1 { font-size: 23px; line-height: 1.13; margin-bottom: 8px; }
  .lf-sub { font-size: 14.5px; line-height: 1.45; margin-bottom: 0; }
  .lf-card { padding: 18px 18px 18px; }
  .lf-progress { margin-bottom: 14px; }
  .lf-q { font-size: 21px; }
  .lf-hint { margin-bottom: 14px; }
  .lf-field { margin-bottom: 11px; }
  .lf-how { margin-top: 14px; }
}

.lf-enter { animation: lf-up 300ms cubic-bezier(.22,1,.36,1) both; }
@keyframes lf-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }

.lf-success { text-align: center; padding: 6px 0 2px; }
.lf-check { width: 78px; height: 78px; border-radius: 50%; background: #dcfce7; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; animation: lf-pop 520ms cubic-bezier(.34,1.56,.64,1) both; }
@keyframes lf-pop { 0% { transform: scale(.4); opacity: 0; } 60% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); } }
.lf-phonebox { background: #f8f9fa; border: 1px solid #e6e9ee; border-radius: 14px; padding: 14px 16px; margin: 18px 0 0; text-align: left; }
.lf-phonebox .v { font-size: 16px; font-weight: 700; color: #1e2b38; }
.lf-phonebox .v a { color: inherit; text-decoration: none; }
.lf-phonebox .s { font-size: 12.5px; line-height: 1.45; color: #6b7a8d; margin-top: 4px; }
`;

// ── helpers ──────────────────────────────────────────────────────────────────
function phoneDigits(v) { return (v || '').replace(/[^0-9]/g, ''); }
function validPhone(v) { const d = phoneDigits(v); return d.length >= 8 && d.length <= 16; }
function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim()); }

function loadDraft() {
  try { const raw = sessionStorage.getItem(DRAFT_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function saveDraft(d) { try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch {} }
function clearDraft() { try { sessionStorage.removeItem(DRAFT_KEY); } catch {} }

const Check = ({ size = 14, color = '#16a34a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);
const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);
const Lock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
);

// ── component ─────────────────────────────────────────────────────────────────
export default function LeadForm() {
  const location = useLocation();
  const draft = useRef(loadDraft()).current;

  const [step, setStep] = useState(draft?.step && draft.step < 7 ? draft.step : 1);
  const [form, setForm] = useState({
    vorname: '', nachname: '', telefon: '', email: '', ort: '',
    datenschutz: false,
    arbeitslos: null, fuehrerschein_b: null, erreichbarkeit: null,
    website: '', // honeypot
    ...(draft?.form || {}),
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState({ pixelId: null, openaiPixelId: null, contactPhone: '02058 / 77 921-60' });

  const sessionId = useRef('lead_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8));
  const eventId = useRef(generateEventId());
  const openaiEventId = useRef(generateEventId());
  const tracking = useRef({ utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: '', fbclid: '', referrer_url: '', landing_url: '', campaign_id: '', adset_id: '', ad_id: '', placement: '' });
  const startedAt = useRef(Date.now());
  const consentAt = useRef(null);
  const stepRef = useRef(step);
  const doneRef = useRef(false);
  const firstInput = useRef(null);

  // ── page setup: title, tracking params, pixel, first step log ──────────────
  useEffect(() => {
    const prev = document.title;
    document.title = PAGE_TITLE;
    const p = new URLSearchParams(location.search);
    // Meta ersetzt Leerzeichen in Namen durch "+" und kodiert das Plus dann noch einmal —
    // hier zurück in Leerzeichen, damit Namen mit den Meta-Insights zusammenpassen.
    const nv = k => (p.get(k) || '').replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();
    tracking.current = {
      // Keine Default-Werte erfinden: fehlt der Parameter, bleibt er leer und der
      // Server leitet die Quelle aus fbclid/Referrer ab (siehe deriveAttribution).
      utm_source: nv('utm_source'),
      utm_medium: nv('utm_medium'),
      utm_campaign: nv('utm_campaign'),
      utm_content: nv('utm_content'),
      utm_term: nv('utm_term'),
      fbclid: p.get('fbclid') || '',
      referrer_url: document.referrer || '',
      landing_url: window.location.href.split('#')[0],
      // Meta-URL-Parameter: {{campaign.id}} {{adset.id}} {{ad.id}} {{placement}}
      campaign_id: p.get('campaign_id') || '',
      adset_id: p.get('adset_id') || '',
      ad_id: p.get('ad_id') || '',
      placement: nv('placement'),
    };
    logStep(stepRef.current);

    fetch('/api/lead/config').then(r => r.json()).then(c => {
      setConfig(prev => ({ ...prev, ...c }));
      // Pixel werden global in components/Tracking.jsx geladen; hier nur die Rufnummer.
    }).catch(() => {});

    const onUnload = () => {
      if (doneRef.current) return;
      fetch('/api/log-step', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ event_type: 'lead_abandoned', metadata: { step: STEP_NAMES[stepRef.current], step_number: stepRef.current, session_id: sessionId.current, ...tracking.current } }),
      }).catch(() => {});
    };
    window.addEventListener('pagehide', onUnload);
    return () => { window.removeEventListener('pagehide', onUnload); document.title = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (step < 7 && !doneRef.current) saveDraft({ step, form: { ...form, website: '' } }); }, [step, form]);
  useEffect(() => {
    stepRef.current = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (step === 1) return; // kein Autofokus beim Laden — Tastatur würde auf dem Handy sofort aufspringen
    const t = setTimeout(() => firstInput.current?.focus?.(), 120);
    return () => clearTimeout(t);
  }, [step]);

  const logStep = useCallback((s, extra = {}) => {
    fetch('/api/log-step', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'lead_step_view', metadata: { step: STEP_NAMES[s] || `lead_step_${s}`, step_number: s, session_id: sessionId.current, ...tracking.current, ...extra } }),
    }).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => { const n = { ...e }; delete n[k]; return n; }); };
  const goTo = (s) => { setStep(s); if (s < 7) logStep(s); };  // 7 = Danke-Screen, wird separat geloggt

  // ── step validation ───────────────────────────────────────────────────────
  const next = () => {
    const e = {};
    if (step === 3) {
      if (!form.vorname.trim()) e.vorname = 'Bitte geben Sie Ihren Vornamen ein.';
      if (!form.nachname.trim()) e.nachname = 'Bitte geben Sie Ihren Nachnamen ein.';
    }
    if (step === 4 && !validEmail(form.email)) e.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    if (step === 5 && !validPhone(form.telefon)) e.telefon = 'Bitte geben Sie eine gültige Telefonnummer ein.';
    if (Object.keys(e).length) { setErrors(e); return; }
    if (step === 1) trackPixelCustom('LeadFormStart', { content_name: 'bewerbung_rueckruf' });
    goTo(step + 1);
  };
  const choose = (key, value, autoNext = true) => {
    set(key, value);
    if (autoNext) setTimeout(() => goTo(step + 1), 220);
  };
  const onKey = (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); step === 6 ? submit() : next(); } };

  // ── submit ────────────────────────────────────────────────────────────────
  const submit = async () => {
    if (loading) return;
    if (!form.datenschutz) {
      setErrors({ datenschutz: 'Bitte bestätigen Sie die Datenschutzhinweise, um Ihre Anfrage zu senden.' });
      return;
    }
    // Nachweis nach Art. 7 Abs. 1 DSGVO
    if (!consentAt.current) consentAt.current = new Date().toISOString();
    setSubmitError('');
    const { fbc, fbp } = getMetaBrowserIds(tracking.current.fbclid);
    const body = {
      vorname: form.vorname.trim(), nachname: form.nachname.trim(),
      telefon: form.telefon.trim(), email: form.email.trim(), ort: form.ort.trim(),
      arbeitslos: form.arbeitslos, fuehrerschein_b: form.fuehrerschein_b,
      erreichbarkeit: form.erreichbarkeit || 'egal',
      hp_xq7: form.website, // Honeypot — leer bei Menschen
      event_id: eventId.current, openai_event_id: openaiEventId.current, session_id: sessionId.current,
      fbc, fbp, ...tracking.current,
      // Kontext fürs Lead-Dashboard (Gerät/Browser/Herkunft ermittelt der Server selbst)
      screen_size: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      browser_lang: (navigator.language || '').slice(0, 20),
      client_tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; } })(),
      time_to_submit: Math.round((Date.now() - startedAt.current) / 1000),
      consent_at: consentAt.current,
    };
    setLoading(true);
    try {
      const res = await fetch('/api/lead', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (res.status === 400 && data.errors) {
        setErrors(data.errors);
        if (data.errors.vorname || data.errors.nachname) goTo(3);
        else if (data.errors.email) goTo(4);
        else if (data.errors.telefon) goTo(5);
        return;
      }
      if (!res.ok || !data.success) throw new Error(data.error || 'Senden fehlgeschlagen.');

      doneRef.current = true;
      clearDraft();
      setResult(data);
      // Ab hier ist der Lead beim Server sicher gespeichert. Kein Tracking-Fehler
      // darf den Nutzer noch eine Fehlermeldung sehen lassen — deshalb gekapselt.
      try {
        // Nutzerdaten an den Pixel geben, bevor das Event feuert. Sonst traegt das
        // Browser-Ereignis nur Cookies, waehrend das Server-Ereignis E-Mail, Telefon
        // und Name mitschickt — Meta erkennt beide dann schlechter als dasselbe Ereignis.
        setMetaUserData({
          email: body.email, telefon: body.telefon, vorname: body.vorname,
          nachname: body.nachname, ort: body.ort, externalId: data.lead_ref,
        });
        // Conversion im Browser melden — jeweils mit derselben ID wie der Server,
        // damit Meta und OpenAI die doppelte Meldung zusammenfuehren statt doppelt zu zaehlen.
        // Kein currency/value: Ein Lead hat keinen Geldwert, und der Server schickte ohnehin
        // keinen mit. Meta hat genau das als „50 % der Waehrungsdaten fehlerhaft" gemeldet.
        trackPixelEvent('Lead', { content_name: 'bewerbung_rueckruf', content_category: 'sprinklertechniker' }, data.event_id || eventId.current);
        trackOpenAiLead(data.openai_event_id || openaiEventId.current, body.email);
      } catch (trackErr) {
        console.warn('[lead] Tracking fehlgeschlagen, Lead ist trotzdem gespeichert:', trackErr?.message);
      }
      fetch('/api/log-step', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'lead_step_view', booking_ref: data.lead_ref, metadata: { step: 'lead_success', step_number: 7, session_id: sessionId.current, ...tracking.current } }),
      }).catch(() => {});
      setStep(7);
    } catch (err) {
      setSubmitError(err.message === 'Failed to fetch' ? 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es noch einmal.' : (err.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es noch einmal.'));
    } finally {
      setLoading(false);
    }
  };

  const pct = Math.round(((step - 1) / TOTAL_STEPS) * 100);
  const reachLabel = REACH_OPTIONS.find(r => r.id === (form.erreichbarkeit || 'egal'));
  const phoneHref = 'tel:' + (config.contactPhone || '').replace(/[^0-9+]/g, '');

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="lf">
      <style>{CSS}</style>
      <div className="lf-wrap">

        <div className="lf-top">
          <img src={LOGO} alt="ZBA – Zentrale Brandschutztechnik-Akademie" className="lf-logo" />
          {step < 7 && <span className="lf-pill"><span className="dot" />Kostenlos &amp; unverbindlich</span>}
        </div>

        {step === 1 && (
          <div className="lf-hero lf-enter">
            <h1 className="lf-h1">Sprinklertechniker/in werden – <em>100 % kostenlos</em> mit Bildungsgutschein.</h1>
            <p className="lf-sub">Zwei kurze Fragen, dann rufen wir Sie an.</p>
          </div>
        )}

        {step === 1 && (
          <ul className="lf-trust lf-enter">
            <li><Check size={12} /><span>Staatlich gefördert</span></li>
            <li><Check size={12} /><span>IHK-Zertifikat</span></li>
            <li><Check size={12} /><span>Unverbindlich</span></li>
          </ul>
        )}

        <div className="lf-card">
          {step < 7 && (
            <div className="lf-progress">
              <div className="lf-bar"><i style={{ width: `${Math.max(pct, 6)}%` }} /></div>
              <span className="lf-step">Schritt {step} / {TOTAL_STEPS}</span>
            </div>
          )}

          {/* 2 — Name */}
          {step === 3 && (
            <div className="lf-enter" key="s3">
              <h2 className="lf-q">Wie heißen Sie?</h2>
              <div className="lf-grid2 lf-field">
                <div>
                  <label className="lf-label" htmlFor="lf-vorname">Vorname</label>
                  <input ref={firstInput} id="lf-vorname" className={`lf-input${errors.vorname ? ' err' : ''}`} value={form.vorname} onChange={e => set('vorname', e.target.value)} onKeyDown={onKey} placeholder="Max" autoComplete="given-name" autoCapitalize="words" enterKeyHint="next" />
                  {errors.vorname && <p className="lf-error">{errors.vorname}</p>}
                </div>
                <div>
                  <label className="lf-label" htmlFor="lf-nachname">Nachname</label>
                  <input id="lf-nachname" className={`lf-input${errors.nachname ? ' err' : ''}`} value={form.nachname} onChange={e => set('nachname', e.target.value)} onKeyDown={onKey} placeholder="Mustermann" autoComplete="family-name" autoCapitalize="words" enterKeyHint="next" />
                  {errors.nachname && <p className="lf-error">{errors.nachname}</p>}
                </div>
              </div>
              {/* Honeypot gegen Bots. Drei Schutzschichten gegen versehentliches Ausfüllen durch
                  Browser-Autofill und Passwortmanager: (1) display:none — LastPass, 1Password,
                  Dashlane, Bitwarden und Chrome überspringen unsichtbare Felder; (2) ein Name ohne
                  jede Bedeutung, damit keine Autofill-Heuristik ("website", "url", "email" …)
                  anspringt; (3) die Ignore-Attribute der gängigen Manager. Und falls doch etwas
                  reinläuft: der Server verwirft den Lead nicht, sondern markiert ihn nur. */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <input type="text" name="hp_xq7" value={form.website} onChange={e => set('website', e.target.value)}
                  tabIndex={-1} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                  data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other" data-dashlane-ignore="true" />
              </div>
              <button type="button" className="lf-btn" onClick={next}>Weiter <Arrow /></button>
              <button type="button" className="lf-back" onClick={() => goTo(2)}>← Zurück</button>
            </div>
          )}

          {/* 3 — Arbeitssuchend */}
          {step === 1 && (
            <div className="lf-enter" key="s1">
              <h2 className="lf-q">Wie ist Ihre Situation gerade?</h2>
              <div className="lf-choice">
                <button type="button" className={`lf-opt${form.arbeitslos === 'ja' ? ' on' : ''}`} onClick={() => choose('arbeitslos', 'ja')}><span className="ic">✅</span><span>Ich bin arbeitssuchend</span></button>
                <button type="button" className={`lf-opt${form.arbeitslos === 'bald' ? ' on' : ''}`} onClick={() => choose('arbeitslos', 'bald')}><span className="ic">⏳</span><span>Bald<span className="sub">Kündigung erhalten oder Vertrag läuft aus</span></span></button>
                <button type="button" className={`lf-opt${form.arbeitslos === 'nein' ? ' on' : ''}`} onClick={() => choose('arbeitslos', 'nein')}><span className="ic">💼</span><span>Ich bin berufstätig<span className="sub">Umschulung ist oft trotzdem möglich</span></span></button>
              </div>
            </div>
          )}

          {/* 4 — Führerschein */}
          {step === 2 && (
            <div className="lf-enter" key="s2">
              <h2 className="lf-q">Haben Sie einen Führerschein Klasse B?</h2>
              <div className="lf-choice cols2">
                <button type="button" className={`lf-opt compact${form.fuehrerschein_b === 'ja' ? ' on' : ''}`} onClick={() => choose('fuehrerschein_b', 'ja')}><span className="ic">🚗</span>Ja</button>
                <button type="button" className={`lf-opt compact${form.fuehrerschein_b === 'nein' ? ' on' : ''}`} onClick={() => choose('fuehrerschein_b', 'nein')}><span className="ic">🚫</span>Nein</button>
              </div>
              <button type="button" className="lf-back" onClick={() => goTo(1)}>← Zurück</button>
            </div>
          )}

          {/* 5 — E-Mail */}
          {step === 4 && (
            <div className="lf-enter" key="s4">
              <h2 className="lf-q">Wie lautet Ihre E-Mail-Adresse?</h2>
              <p className="lf-hint">Kein Newsletter.</p>
              <div className="lf-field">
                <input ref={firstInput} id="lf-email" className={`lf-input${errors.email ? ' err' : ''}`} type="email" inputMode="email" value={form.email} onChange={e => set('email', e.target.value)} onKeyDown={onKey} placeholder="max@beispiel.de" autoComplete="email" autoCapitalize="none" enterKeyHint="next" />
                {errors.email && <p className="lf-error">{errors.email}</p>}
              </div>
              <button type="button" className="lf-btn" onClick={next}>Weiter <Arrow /></button>
              <button type="button" className="lf-back" onClick={() => goTo(3)}>← Zurück</button>
            </div>
          )}

          {/* 6 — Telefon (mit Berater + „so läuft der Anruf") */}
          {step === 5 && (
            <div className="lf-enter" key="s5">
              <h2 className="lf-q">Unter welcher Nummer erreichen wir Sie?</h2>
              <p className="lf-hint">Kein Verkaufsgespräch. Mit Bildungsgutschein zahlen Sie nichts.</p>
              <div className="lf-field">
                <input ref={firstInput} id="lf-telefon" className={`lf-input${errors.telefon ? ' err' : ''}`} type="tel" inputMode="tel" value={form.telefon} onChange={e => set('telefon', e.target.value)} onKeyDown={onKey} placeholder="0151 23456789" autoComplete="tel" enterKeyHint="next" />
                {errors.telefon && <p className="lf-error">{errors.telefon}</p>}
              </div>
              <button type="button" className="lf-btn" onClick={next}>Weiter <Arrow /></button>
              <button type="button" className="lf-back" onClick={() => goTo(4)}>← Zurück</button>
            </div>
          )}

          {/* 7 — Erreichbarkeit + Ort + Submit */}
          {step === 6 && (
            <div className="lf-enter" key="s6">
              <h2 className="lf-q">Wann sollen wir anrufen?</h2>
              <p className="lf-hint">Mo bis Fr.</p>
              <div className="lf-choice cols2" style={{ marginBottom: 16 }}>
                {REACH_OPTIONS.map(o => (
                  <button key={o.id} type="button" className={`lf-opt${form.erreichbarkeit === o.id ? ' on' : ''}`} onClick={() => choose('erreichbarkeit', o.id, false)}>
                    <span className="ic">{o.icon}</span><span>{o.label}<span className="sub">{o.sub}</span></span>
                  </button>
                ))}
              </div>
              <div className="lf-field">
                <label className="lf-label" htmlFor="lf-ort">Wohnort <span style={{ fontWeight: 500, color: '#8a97a8' }}>(optional)</span></label>
                <input ref={firstInput} id="lf-ort" className="lf-input" value={form.ort} onChange={e => set('ort', e.target.value)} onKeyDown={onKey} placeholder="z. B. Wuppertal" autoComplete="address-level2" enterKeyHint="send" />
              </div>
              <label className={`lf-agree${form.datenschutz ? ' on' : ''}${errors.datenschutz ? ' err' : ''}`}>
                <input type="checkbox" checked={form.datenschutz}
                  onChange={e => set('datenschutz', e.target.checked)} />
                <span className="box" aria-hidden="true">
                  {form.datenschutz && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </span>
                <span className="txt">
                  Ich bin einverstanden, dass die ZBA Academy mich zu meiner Anfrage kontaktiert.{' '}
                  <a href="https://zba.academy/datenschutz" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Datenschutz</a>
                </span>
              </label>
              {errors.datenschutz && <p className="lf-error" style={{ marginTop: -8, marginBottom: 10 }}>{errors.datenschutz}</p>}
              {submitError && <p className="lf-error" style={{ marginBottom: 10 }}>{submitError}</p>}
              <button type="button" className="lf-btn cta" onClick={submit} disabled={loading}>
                {loading ? <><span className="spin" /> Wird gesendet…</> : <>📞 Ja, rufen Sie mich an!</>}
              </button>
              <button type="button" className="lf-back" onClick={() => goTo(5)}>← Zurück</button>
            </div>
          )}

          {/* 8 — Success */}
          {step === 7 && (
            <div className="lf-success lf-enter" key="s7">
              <div className="lf-check">
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <h2 className="lf-q" style={{ fontSize: 26 }}>Vielen Dank! Wir rufen Sie an.</h2>
              <p className="lf-hint" style={{ marginBottom: 0 }}>
                Wir melden uns innerhalb von <strong>24 Stunden</strong> (Mo–Fr) unter <strong>{form.telefon}</strong>
                {reachLabel && reachLabel.id !== 'egal' ? <> – <strong>{reachLabel.label.toLowerCase()}</strong>.</> : '.'}
              </p>
              {/* Nur das, was der Besucher jetzt wirklich braucht: dass es geklappt hat,
                  wann wir anrufen, und woran er den Anruf erkennt. Die drei Ablaufschritte
                  und der Termin-Link waren Ballast und lenkten vom Rueckruf ab. */}
              <div className="lf-phonebox">
                <div className="v">Unser Anruf kommt mit Vorwahl <span style={{ letterSpacing: '.04em' }}>02058</span></div>
                <div className="s">Bitte auch dann rangehen, wenn Ihnen die Nummer unbekannt ist.</div>
              </div>
              <p className="lf-hint" style={{ margin: '16px 0 0', fontSize: 13.5 }}>
                Lieber sofort? <a href={phoneHref} style={{ color: '#3E4954', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>{config.contactPhone}</a>
              </p>
              {result?.lead_ref && <p style={{ fontSize: 11, color: '#a3adb9', marginTop: 14 }}>Referenz: {result.lead_ref}</p>}
            </div>
          )}
        </div>

      </div>

      <footer className="lf-foot">
        © {new Date().getFullYear()} ZBA – Zentrale Brandschutztechnik-Akademie GmbH · Kruppstraße 41-45 · 42489 Wülfrath<br />
        <a href="https://zba.academy/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
        <span style={{ margin: '0 8px', opacity: .5 }}>·</span>
        <a href="https://zba.academy/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutz</a>
        <span style={{ margin: '0 8px', opacity: .5 }}>·</span>
        <a href={phoneHref}>{config.contactPhone}</a><br />
        Diese Seite ist nicht Teil der Facebook-Website oder von Meta Platforms, Inc. und wird in keiner Weise von Facebook gesponsert oder unterstützt.
      </footer>
    </div>
  );
}
